import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  approveSmallGroup,
  approveSmallGroupActivity,
  approveSmallGroupEvaluation,
  approveSmallGroupPlan,
  createSmallGroup,
  createSmallGroupActivity,
  createSmallGroupEvaluation,
  createSmallGroupPlan,
  deleteSmallGroup,
  deleteSmallGroupActivity,
  deleteSmallGroupEvaluation,
  deleteSmallGroupPlan,
  fetchSmallGroupActivities,
  fetchSmallGroupEvaluations,
  fetchSmallGroupPlans,
  fetchSmallGroups,
  revokeSmallGroup,
  revokeSmallGroupActivity,
  revokeSmallGroupEvaluation,
  revokeSmallGroupPlan,
  updateSmallGroup,
  updateSmallGroupActivity,
  updateSmallGroupEvaluation,
  updateSmallGroupPlan,
  downloadSmallGroupPdf,
  downloadSmallGroupPlanPdf,
  downloadSmallGroupActivityPdf,
  downloadSmallGroupEvaluationPdf,
} from "../../api/smallGroup";
import { notifyError, reportError } from "../../utils/notify";
import { fetchOrgMembers } from "../../api/org";
import { useCompanyStore } from "../../store/companyStore";
import {
  APPROVAL_TARGET_SMALL_GROUP,
  APPROVAL_TARGET_SMALL_GROUP_EVAL,
  APPROVAL_TARGET_SMALL_GROUP_PLAN,
  APPROVAL_TARGET_SMALL_GROUP_RECORD,
} from "../../types/approval";
import {
  canConfirmInspection,
  companyRoleLabel,
} from "../../types/companyRole";
import {
  createEmptyEducationAttendee,
  defaultEducationAttendees,
  EducationAttendee,
  parseEducationAttendees,
  serializeEducationAttendees,
} from "../../types/education";
import { OrgMemberSummary } from "../../types/org";
import {
  SmallGroup,
  SmallGroupActivity,
  SmallGroupActivityEvaluation,
  SmallGroupActivityEvaluationPayload,
  SmallGroupActivityPayload,
  SmallGroupActivityPlan,
  SmallGroupActivityPlanPayload,
  SmallGroupEvalCriterion,
  SmallGroupMember,
  SmallGroupPayload,
  SmallGroupPlanItem,
  createManualSmallGroupMember,
  createOrgSmallGroupMember,
  parseSmallGroupMembers,
  serializeSmallGroupMembers,
} from "../../types/smallGroup";
import ApprovalCancelButton from "../approval/ApprovalCancelButton";
import ApprovalRequestModal from "../approval/ApprovalRequestModal";
import SignaturePadField from "../common/SignaturePadField";

import "../product/InspectionFormSheet.css";
import "../product/NonconformanceReportPanel.css";
import "./CleanActivityPanel.css";
import "./QualityShell.css";
import "./SmallGroupPanel.css";
import "./EducationAnnualPlanTab.css";

type Tab = "group" | "plan" | "record" | "evaluation";

const UNASSIGNED_DEPT = "__UNASSIGNED__";

type GroupView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; groupId: number }
  | { type: "detail"; groupId: number };

type PlanView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; planId: number }
  | { type: "detail"; planId: number };

type RecordView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; activityId: number }
  | { type: "detail"; activityId: number };

type EvalView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; evaluationId: number }
  | { type: "detail"; evaluationId: number };

const GROUP_LIST: GroupView = { type: "list" };
const PLAN_LIST: PlanView = { type: "list" };
const RECORD_LIST: RecordView = { type: "list" };
const EVAL_LIST: EvalView = { type: "list" };

type ApprovalStamp = {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  canRevoke?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
};

const EMPTY_STAMP: ApprovalStamp = {
  authorName: null,
  reviewerName: null,
  approverName: null,
  canRevoke: false,
  approvalInProgress: false,
  pendingApprovalRequestId: null,
  canCancelApproval: false,
};

function isApproved(status?: string | null) {
  return (status || "").toUpperCase() === "APPROVED";
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ["작성", authorName],
          ["검토", reviewerName],
          ["승인", approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span
            style={{
              color: value ? "#1f2a37" : "#98a2b3",
              fontWeight: value ? 600 : 400,
            }}
          >
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

function stampFrom(row: ApprovalStamp): ApprovalStamp {
  return {
    authorName: row.authorName ?? null,
    reviewerName: row.reviewerName ?? null,
    approverName: row.approverName ?? null,
    canRevoke: row.canRevoke ?? false,
    approvalInProgress: row.approvalInProgress ?? false,
    pendingApprovalRequestId: row.pendingApprovalRequestId ?? null,
    canCancelApproval: row.canCancelApproval ?? false,
  };
}

function apiErrorMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message || fallback
  );
}

function memberName(m: OrgMemberSummary) {
  return m.displayName || m.nickname || `사용자 ${m.userId}`;
}

function memberMeta(m: OrgMemberSummary) {
  const job = m.jobTitle?.trim() || companyRoleLabel(m.role);
  const dept = m.departmentName?.trim() || "미배치";
  return `${job} · ${dept}`;
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function parsePlanItems(json?: string | null): SmallGroupPlanItem[] {
  if (!json?.trim()) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((row: Partial<SmallGroupPlanItem>) => ({
      id: row.id || newId(),
      topic: row.topic || "",
      schedule: row.schedule || "",
      method: row.method || "",
      owner: row.owner || "",
    }));
  } catch {
    return [];
  }
}

function defaultCriteria(): SmallGroupEvalCriterion[] {
  return [
    { id: newId(), criterion: "목표 달성도", score: "", comment: "" },
    { id: newId(), criterion: "참여도", score: "", comment: "" },
    { id: newId(), criterion: "개선 효과", score: "", comment: "" },
    { id: newId(), criterion: "지속 가능성", score: "", comment: "" },
  ];
}

function parseCriteria(json?: string | null): SmallGroupEvalCriterion[] {
  if (!json?.trim()) return defaultCriteria();
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultCriteria();
    return parsed.map((row: Partial<SmallGroupEvalCriterion>) => ({
      id: row.id || newId(),
      criterion: row.criterion || "",
      score: row.score || "",
      comment: row.comment || "",
    }));
  } catch {
    return defaultCriteria();
  }
}

function SmallGroupPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const [tab, setTab] = useState<Tab>("group");
  const [error, setError] = useState("");
  const [approvalMessage, setApprovalMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const [groups, setGroups] = useState<SmallGroup[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMemberSummary[]>([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [manualMemberName, setManualMemberName] = useState("");
  const [manualMemberDept, setManualMemberDept] = useState("");
  const [groupKeyword, setGroupKeyword] = useState("");
  const [groupView, setGroupView] = useState<GroupView>(GROUP_LIST);
  const [groupHint, setGroupHint] = useState("");
  const [groupStamp, setGroupStamp] = useState<ApprovalStamp>(EMPTY_STAMP);
  const [groupApprovalOpen, setGroupApprovalOpen] = useState(false);
  const [groupForm, setGroupForm] = useState<SmallGroupPayload>({
    groupNo: "",
    groupName: "",
    leaderUserId: null,
    secretaryUserId: null,
    members: [],
    theme: "",
    purpose: "",
    activityGoal: "",
    meetingCycle: "주 1회",
    meetingPlace: "",
    formedDate: new Date().toISOString().slice(0, 10),
    closedDate: "",
    status: "ACTIVE",
    remark: "",
  });

  const [plans, setPlans] = useState<SmallGroupActivityPlan[]>([]);
  const [planFilterGroupId, setPlanFilterGroupId] = useState<number | "">("");
  const [planView, setPlanView] = useState<PlanView>(PLAN_LIST);
  const [planHint, setPlanHint] = useState("");
  const [planStamp, setPlanStamp] = useState<ApprovalStamp>(EMPTY_STAMP);
  const [planApprovalOpen, setPlanApprovalOpen] = useState(false);
  const [planItems, setPlanItems] = useState<SmallGroupPlanItem[]>([]);
  const [planForm, setPlanForm] = useState<SmallGroupActivityPlanPayload>({
    groupId: 0,
    planNo: "",
    planYear: new Date().getFullYear(),
    title: "",
    objective: "",
    periodFrom: "",
    periodTo: "",
    expectedEffect: "",
    preparedBy: "",
    status: "DRAFT",
    remark: "",
  });

  const [records, setRecords] = useState<SmallGroupActivity[]>([]);
  const [recordFilterGroupId, setRecordFilterGroupId] = useState<number | "">(
    "",
  );
  const [recordView, setRecordView] = useState<RecordView>(RECORD_LIST);
  const [recordHint, setRecordHint] = useState("");
  const [recordStamp, setRecordStamp] = useState<ApprovalStamp>(EMPTY_STAMP);
  const [recordApprovalOpen, setRecordApprovalOpen] = useState(false);
  const [recordForm, setRecordForm] = useState<SmallGroupActivityPayload>({
    groupId: 0,
    recordNo: "",
    activityDate: new Date().toISOString().slice(0, 10),
    title: "",
    location: "",
    attendees: "[]",
    content: "",
    resultText: "",
    nextPlan: "",
    status: "DRAFT",
    remark: "",
  });
  const [recordAttendees, setRecordAttendees] = useState<EducationAttendee[]>(
    defaultEducationAttendees(),
  );

  const [evaluations, setEvaluations] = useState<
    SmallGroupActivityEvaluation[]
  >([]);
  const [evalFilterGroupId, setEvalFilterGroupId] = useState<number | "">("");
  const [evalView, setEvalView] = useState<EvalView>(EVAL_LIST);
  const [evalHint, setEvalHint] = useState("");
  const [evalStamp, setEvalStamp] = useState<ApprovalStamp>(EMPTY_STAMP);
  const [evalApprovalOpen, setEvalApprovalOpen] = useState(false);
  const [evalCriteria, setEvalCriteria] =
    useState<SmallGroupEvalCriterion[]>(defaultCriteria());
  const [evalForm, setEvalForm] = useState<SmallGroupActivityEvaluationPayload>(
    {
      groupId: 0,
      evaluationNo: "",
      evaluationDate: new Date().toISOString().slice(0, 10),
      title: "",
      periodFrom: "",
      periodTo: "",
      scoreText: "",
      strengths: "",
      improvements: "",
      overallComment: "",
      evaluator: "",
      status: "DRAFT",
      remark: "",
    },
  );

  const loadGroups = useCallback(async (q?: string) => {
    try {
      setGroups(await fetchSmallGroups(q));
      setError("");
    } catch {
      reportError(setError, "소집단 목록을 불러오지 못했습니다.");
    }
  }, []);

  const loadOrgMembers = useCallback(async () => {
    try {
      setOrgMembers(await fetchOrgMembers());
    } catch {
      // form open시에도 다시 시도
    }
  }, []);

  const loadPlans = useCallback(async (groupId?: number | "") => {
    try {
      setPlans(
        await fetchSmallGroupPlans(
          groupId === "" || groupId == null ? undefined : groupId,
        ),
      );
      setError("");
    } catch {
      reportError(setError, "활동 계획서를 불러오지 못했습니다.");
    }
  }, []);

  const loadRecords = useCallback(async (groupId?: number | "") => {
    try {
      setRecords(
        await fetchSmallGroupActivities(
          groupId === "" || groupId == null ? undefined : groupId,
        ),
      );
      setError("");
    } catch {
      reportError(setError, "활동 기록서를 불러오지 못했습니다.");
    }
  }, []);

  const loadEvaluations = useCallback(async (groupId?: number | "") => {
    try {
      setEvaluations(
        await fetchSmallGroupEvaluations(
          groupId === "" || groupId == null ? undefined : groupId,
        ),
      );
      setError("");
    } catch {
      reportError(setError, "활동 평가표를 불러오지 못했습니다.");
    }
  }, []);

  useEffect(() => {
    void loadGroups();
    void loadOrgMembers();
    void loadPlans();
    void loadRecords();
    void loadEvaluations();
  }, [
    companyId,
    loadGroups,
    loadOrgMembers,
    loadPlans,
    loadRecords,
    loadEvaluations,
  ]);

  const groupEditId =
    groupView.type === "edit" || groupView.type === "detail"
      ? groupView.groupId
      : null;
  const planEditId =
    planView.type === "edit" || planView.type === "detail"
      ? planView.planId
      : null;
  const recordEditId =
    recordView.type === "edit" || recordView.type === "detail"
      ? recordView.activityId
      : null;
  const evalEditId =
    evalView.type === "edit" || evalView.type === "detail"
      ? evalView.evaluationId
      : null;
  const showGroupForm = groupView.type !== "list";
  const showPlanForm = planView.type !== "list";
  const showRecordForm = recordView.type !== "list";
  const showEvalForm = evalView.type !== "list";
  const isGroupDetail = groupView.type === "detail";
  const isPlanDetail = planView.type === "detail";
  const isRecordDetail = recordView.type === "detail";
  const isEvalDetail = evalView.type === "detail";
  const isGroupEditing =
    groupView.type === "create" || groupView.type === "edit";
  const isPlanEditing = planView.type === "create" || planView.type === "edit";
  const isRecordEditing =
    recordView.type === "create" || recordView.type === "edit";
  const isEvalEditing = evalView.type === "create" || evalView.type === "edit";
  const recordLocked = !isRecordEditing;

  const recordDepartmentOptions = useMemo(() => {
    const map = new Map<
      string,
      { key: string; label: string; departmentId: number | null }
    >();
    for (const m of orgMembers) {
      const id = m.departmentId ?? null;
      const label = m.departmentName?.trim() || "미배치";
      const key = id != null ? `d-${id}` : UNASSIGNED_DEPT;
      if (!map.has(key)) map.set(key, { key, label, departmentId: id });
    }
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "ko"),
    );
  }, [orgMembers]);

  const recordMembersByDeptKey = useCallback(
    (deptKey: string) => {
      return orgMembers.filter((m) => {
        const key =
          m.departmentId != null ? `d-${m.departmentId}` : UNASSIGNED_DEPT;
        return key === deptKey;
      });
    },
    [orgMembers],
  );

  const recordDeptKeyOfRow = (row: EducationAttendee) => {
    if (row.departmentId != null) return `d-${row.departmentId}`;
    if (!row.dept?.trim() || row.dept === "미배치") {
      if (row.userId != null) {
        const m = orgMembers.find((x) => x.userId === row.userId);
        if (m)
          return m.departmentId != null
            ? `d-${m.departmentId}`
            : UNASSIGNED_DEPT;
      }
      if (row.dept?.trim() === "미배치" || !row.dept?.trim())
        return row.dept || row.userId ? UNASSIGNED_DEPT : "";
      const byName = recordDepartmentOptions.find((d) => d.label === row.dept);
      return byName?.key || "";
    }
    const byName = recordDepartmentOptions.find((d) => d.label === row.dept);
    return byName?.key || "";
  };

  const recordSelectedUserIds = useMemo(() => {
    const set = new Set<number>();
    for (const row of recordAttendees) {
      if (row.userId != null) set.add(row.userId);
    }
    return set;
  }, [recordAttendees]);

  const patchRecordAttendee = (
    idx: number,
    patch: Partial<EducationAttendee>,
  ) => {
    setRecordAttendees((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );
  };

  const filteredOrgMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return orgMembers;
    return orgMembers.filter((m) => {
      const hay = [
        memberName(m),
        m.employeeNo || "",
        m.jobTitle || "",
        m.departmentName || "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [orgMembers, memberQuery]);

  useEffect(() => {
    const t = window.setTimeout(() => void loadGroups(groupKeyword), 250);
    return () => window.clearTimeout(t);
  }, [groupKeyword, loadGroups]);

  useEffect(() => {
    void loadPlans(planFilterGroupId);
  }, [planFilterGroupId, loadPlans]);

  useEffect(() => {
    void loadRecords(recordFilterGroupId);
  }, [recordFilterGroupId, loadRecords]);

  useEffect(() => {
    void loadEvaluations(evalFilterGroupId);
  }, [evalFilterGroupId, loadEvaluations]);

  useEffect(() => {
    if (!showGroupForm || groupEditId == null) return;
    const latest = groups.find((r) => r.groupId === groupEditId);
    if (!latest) return;
    setGroupStamp(stampFrom(latest));
    setGroupForm((p) => ({ ...p, status: latest.status || "ACTIVE" }));
  }, [groups, groupEditId, showGroupForm]);

  useEffect(() => {
    if (!showPlanForm || planEditId == null) return;
    const latest = plans.find((r) => r.planId === planEditId);
    if (!latest) return;
    setPlanStamp(stampFrom(latest));
    setPlanForm((p) => ({ ...p, status: latest.status || "DRAFT" }));
  }, [plans, planEditId, showPlanForm]);

  useEffect(() => {
    if (!showRecordForm || recordEditId == null) return;
    const latest = records.find((r) => r.activityId === recordEditId);
    if (!latest) return;
    setRecordStamp(stampFrom(latest));
    setRecordForm((p) => ({ ...p, status: latest.status || "DRAFT" }));
  }, [records, recordEditId, showRecordForm]);

  useEffect(() => {
    if (!showEvalForm || evalEditId == null) return;
    const latest = evaluations.find((r) => r.evaluationId === evalEditId);
    if (!latest) return;
    setEvalStamp(stampFrom(latest));
    setEvalForm((p) => ({ ...p, status: latest.status || "DRAFT" }));
  }, [evaluations, evalEditId, showEvalForm]);

  const resetFormsOnTab = () => {
    setGroupView(GROUP_LIST);
    setPlanView(PLAN_LIST);
    setRecordView(RECORD_LIST);
    setEvalView(EVAL_LIST);
    setGroupApprovalOpen(false);
    setPlanApprovalOpen(false);
    setRecordApprovalOpen(false);
    setEvalApprovalOpen(false);
    setError("");
    setApprovalMessage("");
  };

  const upsertOrgMember = (
    prev: SmallGroupMember[],
    m: OrgMemberSummary,
  ): SmallGroupMember[] => {
    const next = createOrgSmallGroupMember({
      userId: m.userId,
      name: memberName(m),
      dept: m.departmentName || "",
    });
    const without = prev.filter((x) => x.userId !== m.userId);
    return [...without, next];
  };

  const removeMember = (
    prev: SmallGroupMember[],
    member: SmallGroupMember,
  ): SmallGroupMember[] => {
    if (member.userId != null) {
      return prev.filter((x) => x.userId !== member.userId);
    }
    return prev.filter((x) => x.id !== member.id);
  };

  const openGroupCreate = () => {
    setGroupStamp(EMPTY_STAMP);
    setGroupApprovalOpen(false);
    setMemberQuery("");
    setManualMemberName("");
    setManualMemberDept("");
    setGroupForm({
      groupNo: "",
      groupName: "",
      leaderUserId: null,
      secretaryUserId: null,
      members: [],
      theme: "",
      purpose: "",
      activityGoal: "",
      meetingCycle: "주 1회",
      meetingPlace: "",
      formedDate: new Date().toISOString().slice(0, 10),
      closedDate: "",
      status: "ACTIVE",
      remark: "",
    });
    setGroupHint("저장하면 상세보기로 이동합니다.");
    setGroupView({ type: "create" });
    void loadOrgMembers();
  };

  const fillGroupForm = (row: SmallGroup) => {
    setGroupStamp(stampFrom(row));
    setGroupApprovalOpen(false);
    setGroupHint("");
    setMemberQuery("");
    setManualMemberName("");
    setManualMemberDept("");
    setGroupForm({
      groupNo: row.groupNo || "",
      groupName: row.groupName,
      leaderUserId: row.leaderUserId ?? null,
      secretaryUserId: row.secretaryUserId ?? null,
      members: parseSmallGroupMembers(row),
      theme: row.theme || "",
      purpose: row.purpose || "",
      activityGoal: row.activityGoal || "",
      meetingCycle: row.meetingCycle || "",
      meetingPlace: row.meetingPlace || "",
      formedDate: row.formedDate || "",
      closedDate: row.closedDate || "",
      status: row.status || "ACTIVE",
      remark: row.remark || "",
    });
    void loadOrgMembers();
  };

  const openGroupDetail = (row: SmallGroup) => {
    fillGroupForm(row);
    setGroupView({ type: "detail", groupId: row.groupId });
  };

  const saveGroup = async (e: FormEvent) => {
    e.preventDefault();
    if (isApproved(groupForm.status)) {
      reportError(setError, "승인 완료된 소집단은 수정할 수 없습니다.");
      return;
    }
    if (!groupForm.groupName.trim()) {
      reportError(setError, "소집단명을 입력해 주세요.");
      return;
    }
    if (!groupForm.leaderUserId) {
      reportError(setError, "리더를 선택해 주세요.");
      return;
    }
    setSaving(true);
    try {
      const members = serializeSmallGroupMembers(groupForm.members || []);
      const payload: SmallGroupPayload = {
        ...groupForm,
        members,
        memberUserIds: undefined,
        closedDate:
          groupForm.status === "CLOSED"
            ? groupForm.closedDate || new Date().toISOString().slice(0, 10)
            : undefined,
      };
      let saved: SmallGroup;
      if (groupEditId != null) {
        saved = await updateSmallGroup(groupEditId, payload);
      } else {
        saved = await createSmallGroup(payload);
        window.alert(`소집단번호 ${saved.groupNo || "-"}로 등록되었습니다.`);
      }
      fillGroupForm(saved);
      setGroupForm((p) => ({
        ...p,
        groupNo: saved.groupNo || p.groupNo,
        status: saved.status || p.status,
      }));
      setGroupStamp(stampFrom(saved));
      setGroupView({ type: "detail", groupId: saved.groupId });
      await loadGroups(groupKeyword);
    } catch {
      reportError(setError, "소집단 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const approveGroup = async () => {
    if (groupEditId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "소집단을 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveSmallGroup(groupEditId);
      setGroupStamp(stampFrom(saved));
      setGroupForm((p) => ({ ...p, status: saved.status || "APPROVED" }));
      setApprovalMessage("바로 승인되었습니다.");
      await loadGroups(groupKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revokeGroup = async () => {
    if (groupEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeSmallGroup(groupEditId);
      setGroupStamp(stampFrom(saved));
      setGroupForm((p) => ({ ...p, status: saved.status || "ACTIVE" }));
      setApprovalMessage("승인이 철회되었습니다.");
      await loadGroups(groupKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const downloadGroupPdf = async () => {
    if (groupEditId == null) return;
    try {
      setPdfDownloading(true);
      const name =
        groupForm.groupNo?.trim() ||
        groupForm.groupName?.trim() ||
        String(groupEditId);
      await downloadSmallGroupPdf(groupEditId, `소집단_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const openPlanCreate = () => {
    const year = new Date().getFullYear();
    setPlanStamp(EMPTY_STAMP);
    setPlanApprovalOpen(false);
    setPlanItems([
      { id: newId(), topic: "", schedule: "", method: "", owner: "" },
    ]);
    setPlanForm({
      groupId: groups[0]?.groupId || 0,
      planNo: "",
      planYear: year,
      title: `${year}년 활동 계획서`,
      objective: "",
      periodFrom: "",
      periodTo: "",
      expectedEffect: "",
      preparedBy: "",
      status: "DRAFT",
      remark: "",
    });
    setPlanHint("저장하면 상세보기로 이동합니다.");
    setPlanView({ type: "create" });
  };

  const fillPlanForm = (row: SmallGroupActivityPlan) => {
    setPlanStamp(stampFrom(row));
    setPlanApprovalOpen(false);
    setPlanHint("");
    setPlanItems(parsePlanItems(row.itemsJson));
    setPlanForm({
      groupId: row.groupId,
      planNo: row.planNo || "",
      planYear: row.planYear || new Date().getFullYear(),
      title: row.title || "",
      objective: row.objective || "",
      periodFrom: row.periodFrom || "",
      periodTo: row.periodTo || "",
      expectedEffect: row.expectedEffect || "",
      preparedBy: row.preparedBy || "",
      status: row.status || "DRAFT",
      remark: row.remark || "",
    });
  };

  const openPlanDetail = (row: SmallGroupActivityPlan) => {
    fillPlanForm(row);
    setPlanView({ type: "detail", planId: row.planId });
  };

  const savePlan = async (e: FormEvent) => {
    e.preventDefault();
    if (isApproved(planForm.status)) {
      reportError(setError, "승인 완료된 계획서는 수정할 수 없습니다.");
      return;
    }
    if (!planForm.groupId) {
      reportError(setError, "소집단을 선택해 주세요.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...planForm, itemsJson: JSON.stringify(planItems) };
      let saved: SmallGroupActivityPlan;
      if (planEditId != null) {
        saved = await updateSmallGroupPlan(planEditId, payload);
      } else {
        saved = await createSmallGroupPlan(payload);
        window.alert(`계획서번호 ${saved.planNo || "-"}로 등록되었습니다.`);
      }
      fillPlanForm(saved);
      setPlanForm((p) => ({
        ...p,
        planNo: saved.planNo || p.planNo,
        status: saved.status || p.status,
      }));
      setPlanStamp(stampFrom(saved));
      setPlanView({ type: "detail", planId: saved.planId });
      await loadPlans(planFilterGroupId);
    } catch {
      reportError(setError, "활동 계획서 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const approvePlan = async () => {
    if (planEditId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "계획서를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveSmallGroupPlan(planEditId);
      setPlanStamp(stampFrom(saved));
      setPlanForm((p) => ({ ...p, status: saved.status || "APPROVED" }));
      setApprovalMessage("바로 승인되었습니다.");
      await loadPlans(planFilterGroupId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revokePlan = async () => {
    if (planEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeSmallGroupPlan(planEditId);
      setPlanStamp(stampFrom(saved));
      setPlanForm((p) => ({ ...p, status: saved.status || "DRAFT" }));
      setApprovalMessage("승인이 철회되었습니다.");
      await loadPlans(planFilterGroupId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const downloadPlanPdf = async () => {
    if (planEditId == null) return;
    try {
      setPdfDownloading(true);
      const name = planForm.planNo?.trim() || String(planEditId);
      await downloadSmallGroupPlanPdf(planEditId, `소집단활동계획_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const openRecordCreate = () => {
    setRecordStamp(EMPTY_STAMP);
    setRecordApprovalOpen(false);
    setRecordForm({
      groupId: groups[0]?.groupId || 0,
      recordNo: "",
      activityDate: new Date().toISOString().slice(0, 10),
      title: "",
      location: "",
      attendees: "[]",
      content: "",
      resultText: "",
      nextPlan: "",
      status: "DRAFT",
      remark: "",
    });
    setRecordAttendees(defaultEducationAttendees());
    setRecordHint("저장하면 상세보기로 이동합니다.");
    setRecordView({ type: "create" });
  };

  const fillRecordForm = (row: SmallGroupActivity) => {
    setRecordStamp(stampFrom(row));
    setRecordApprovalOpen(false);
    setRecordHint("");
    const attendees = parseEducationAttendees(row.attendees);
    setRecordAttendees(attendees);
    setRecordForm({
      groupId: row.groupId,
      recordNo: row.recordNo || "",
      activityDate: row.activityDate || "",
      title: row.title || "",
      location: row.location || "",
      attendees: serializeEducationAttendees(attendees),
      content: row.content || "",
      resultText: row.resultText || "",
      nextPlan: row.nextPlan || "",
      status: row.status || "DRAFT",
      remark: row.remark || "",
    });
  };

  const openRecordDetail = (row: SmallGroupActivity) => {
    fillRecordForm(row);
    setRecordView({ type: "detail", activityId: row.activityId });
  };

  const saveRecord = async (e: FormEvent) => {
    e.preventDefault();
    if (isApproved(recordForm.status)) {
      reportError(setError, "승인 완료된 기록서는 수정할 수 없습니다.");
      return;
    }
    if (!recordForm.groupId) {
      reportError(setError, "소집단을 선택해 주세요.");
      return;
    }
    if (!recordForm.title.trim()) {
      reportError(setError, "활동명을 입력해 주세요.");
      return;
    }
    setSaving(true);
    try {
      const payload: SmallGroupActivityPayload = {
        ...recordForm,
        attendees: serializeEducationAttendees(recordAttendees),
      };
      let saved: SmallGroupActivity;
      if (recordEditId != null) {
        saved = await updateSmallGroupActivity(recordEditId, payload);
      } else {
        saved = await createSmallGroupActivity(payload);
        window.alert(`기록번호 ${saved.recordNo || "-"}로 등록되었습니다.`);
      }
      fillRecordForm(saved);
      setRecordForm((p) => ({
        ...p,
        recordNo: saved.recordNo || p.recordNo,
        status: saved.status || p.status,
      }));
      setRecordStamp(stampFrom(saved));
      setRecordView({ type: "detail", activityId: saved.activityId });
      await loadRecords(recordFilterGroupId);
    } catch {
      reportError(setError, "활동 기록서 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const approveRecord = async () => {
    if (recordEditId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "기록서를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveSmallGroupActivity(recordEditId);
      setRecordStamp(stampFrom(saved));
      setRecordForm((p) => ({ ...p, status: saved.status || "APPROVED" }));
      setApprovalMessage("바로 승인되었습니다.");
      await loadRecords(recordFilterGroupId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revokeRecord = async () => {
    if (recordEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeSmallGroupActivity(recordEditId);
      setRecordStamp(stampFrom(saved));
      setRecordForm((p) => ({ ...p, status: saved.status || "DRAFT" }));
      setApprovalMessage("승인이 철회되었습니다.");
      await loadRecords(recordFilterGroupId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const downloadRecordPdf = async () => {
    if (recordEditId == null) return;
    try {
      setPdfDownloading(true);
      const name =
        recordForm.recordNo?.trim() ||
        recordForm.title?.trim() ||
        String(recordEditId);
      await downloadSmallGroupActivityPdf(
        recordEditId,
        `소집단활동_${name}.pdf`,
      );
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const openEvalCreate = () => {
    setEvalStamp(EMPTY_STAMP);
    setEvalApprovalOpen(false);
    setEvalCriteria(defaultCriteria());
    setEvalForm({
      groupId: groups[0]?.groupId || 0,
      evaluationNo: "",
      evaluationDate: new Date().toISOString().slice(0, 10),
      title: "활동 평가표",
      periodFrom: "",
      periodTo: "",
      scoreText: "",
      strengths: "",
      improvements: "",
      overallComment: "",
      evaluator: "",
      status: "DRAFT",
      remark: "",
    });
    setEvalHint("저장하면 상세보기로 이동합니다.");
    setEvalView({ type: "create" });
  };

  const fillEvalForm = (row: SmallGroupActivityEvaluation) => {
    setEvalStamp(stampFrom(row));
    setEvalApprovalOpen(false);
    setEvalHint("");
    setEvalCriteria(parseCriteria(row.criteriaJson));
    setEvalForm({
      groupId: row.groupId,
      evaluationNo: row.evaluationNo || "",
      evaluationDate: row.evaluationDate || "",
      title: row.title || "",
      periodFrom: row.periodFrom || "",
      periodTo: row.periodTo || "",
      scoreText: row.scoreText || "",
      strengths: row.strengths || "",
      improvements: row.improvements || "",
      overallComment: row.overallComment || "",
      evaluator: row.evaluator || "",
      status: row.status || "DRAFT",
      remark: row.remark || "",
    });
  };

  const openEvalDetail = (row: SmallGroupActivityEvaluation) => {
    fillEvalForm(row);
    setEvalView({ type: "detail", evaluationId: row.evaluationId });
  };

  const saveEval = async (e: FormEvent) => {
    e.preventDefault();
    if (isApproved(evalForm.status)) {
      reportError(setError, "승인 완료된 평가표는 수정할 수 없습니다.");
      return;
    }
    if (!evalForm.groupId) {
      reportError(setError, "소집단을 선택해 주세요.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...evalForm,
        criteriaJson: JSON.stringify(evalCriteria),
      };
      let saved: SmallGroupActivityEvaluation;
      if (evalEditId != null) {
        saved = await updateSmallGroupEvaluation(evalEditId, payload);
      } else {
        saved = await createSmallGroupEvaluation(payload);
        window.alert(
          `평가표번호 ${saved.evaluationNo || "-"}로 등록되었습니다.`,
        );
      }
      fillEvalForm(saved);
      setEvalForm((p) => ({
        ...p,
        evaluationNo: saved.evaluationNo || p.evaluationNo,
        status: saved.status || p.status,
      }));
      setEvalStamp(stampFrom(saved));
      setEvalView({ type: "detail", evaluationId: saved.evaluationId });
      await loadEvaluations(evalFilterGroupId);
    } catch {
      reportError(setError, "활동 평가표 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const approveEval = async () => {
    if (evalEditId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "평가표를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveSmallGroupEvaluation(evalEditId);
      setEvalStamp(stampFrom(saved));
      setEvalForm((p) => ({ ...p, status: saved.status || "APPROVED" }));
      setApprovalMessage("바로 승인되었습니다.");
      await loadEvaluations(evalFilterGroupId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revokeEval = async () => {
    if (evalEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeSmallGroupEvaluation(evalEditId);
      setEvalStamp(stampFrom(saved));
      setEvalForm((p) => ({ ...p, status: saved.status || "DRAFT" }));
      setApprovalMessage("승인이 철회되었습니다.");
      await loadEvaluations(evalFilterGroupId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const downloadEvalPdf = async () => {
    if (evalEditId == null) return;
    try {
      setPdfDownloading(true);
      const name = evalForm.evaluationNo?.trim() || String(evalEditId);
      await downloadSmallGroupEvaluationPdf(
        evalEditId,
        `소집단활동평가_${name}.pdf`,
      );
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const groupSelect = (value: number, onChange: (id: number) => void) => (
    <select
      value={value || ""}
      onChange={(e) => onChange(Number(e.target.value))}
      required
    >
      <option value="" disabled>
        선택
      </option>
      {groups.map((g) => (
        <option key={g.groupId} value={g.groupId}>
          {g.groupName}
        </option>
      ))}
    </select>
  );

  const groupFilter = (
    value: number | "",
    onChange: (v: number | "") => void,
  ) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
      style={{
        height: 34,
        borderRadius: 8,
        border: "1px solid #cbd5e1",
        padding: "0 8px",
      }}
    >
      <option value="">전체 소집단</option>
      {groups.map((g) => (
        <option key={g.groupId} value={g.groupId}>
          {g.groupName}
        </option>
      ))}
    </select>
  );

  const renderGroupDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(groupForm.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadGroupPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {groupStamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revokeGroup()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {groupStamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={groupStamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          await loadGroups();
        }}
      />
      {!isApproved(groupForm.status) && !groupStamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (groupEditId == null) return;
              setGroupHint("저장하면 상세보기로 이동합니다.");
              setGroupView({
                type: "edit",
                groupId: groupEditId,
              });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setGroupApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approveGroup()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (groupEditId == null) return;
              if (
                !window.confirm(
                  "소집단과 관련 계획서·기록서·평가표가 함께 삭제됩니다. 계속할까요?",
                )
              )
                return;
              void deleteSmallGroup(groupEditId).then(() => {
                setGroupView({ type: "list" });
                void loadGroups(groupKeyword);
                void loadPlans(planFilterGroupId);
                void loadRecords(recordFilterGroupId);
                void loadEvaluations(evalFilterGroupId);
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setGroupView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderGroupEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        저장
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          groupView.type === "edit" && groupEditId != null
            ? setGroupView({ type: "detail", groupId: groupEditId })
            : setGroupView({ type: "list" })
        }
      >
        {groupView.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  const renderPlanDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(planForm.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadPlanPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {planStamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revokePlan()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {planStamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={planStamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          await loadPlans();
        }}
      />
      {!isApproved(planForm.status) && !planStamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (planEditId == null) return;
              setPlanHint("저장하면 상세보기로 이동합니다.");
              setPlanView({ type: "edit", planId: planEditId });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setPlanApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approvePlan()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (planEditId == null) return;
              if (!window.confirm("삭제할까요?")) return;
              void deleteSmallGroupPlan(planEditId).then(() => {
                setPlanView({ type: "list" });
                void loadPlans(planFilterGroupId);
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setPlanView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderPlanEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        저장
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          planView.type === "edit" && planEditId != null
            ? setPlanView({ type: "detail", planId: planEditId })
            : setPlanView({ type: "list" })
        }
      >
        {planView.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  const renderRecordDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(recordForm.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadRecordPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {recordStamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revokeRecord()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {recordStamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={recordStamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          await loadRecords();
        }}
      />
      {!isApproved(recordForm.status) && !recordStamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (recordEditId == null) return;
              setRecordHint("저장하면 상세보기로 이동합니다.");
              setRecordView({
                type: "edit",
                activityId: recordEditId,
              });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setRecordApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approveRecord()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (recordEditId == null) return;
              if (!window.confirm("삭제할까요?")) return;
              void deleteSmallGroupActivity(recordEditId).then(() => {
                setRecordView({ type: "list" });
                void loadRecords(recordFilterGroupId);
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setRecordView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderRecordEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        저장
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          recordView.type === "edit" && recordEditId != null
            ? setRecordView({
                type: "detail",
                activityId: recordEditId,
              })
            : setRecordView({ type: "list" })
        }
      >
        {recordView.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  const renderEvalDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(evalForm.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadEvalPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {evalStamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revokeEval()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {evalStamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={evalStamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          await loadEvaluations();
        }}
      />
      {!isApproved(evalForm.status) && !evalStamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (evalEditId == null) return;
              setEvalHint("저장하면 상세보기로 이동합니다.");
              setEvalView({
                type: "edit",
                evaluationId: evalEditId,
              });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setEvalApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approveEval()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (evalEditId == null) return;
              if (!window.confirm("삭제할까요?")) return;
              void deleteSmallGroupEvaluation(evalEditId).then(() => {
                setEvalView({ type: "list" });
                void loadEvaluations(evalFilterGroupId);
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setEvalView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderEvalEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        저장
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          evalView.type === "edit" && evalEditId != null
            ? setEvalView({
                type: "detail",
                evaluationId: evalEditId,
              })
            : setEvalView({ type: "list" })
        }
      >
        {evalView.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  return (
    <main className="quality-shell sg-panel">
      <div className="quality-shell__toolbar">
        <div>
          <h2>소집단</h2>
          <p>등록 · 활동 계획서 · 활동 기록서 · 활동 평가표</p>
        </div>
      </div>

      <div className="quality-tabs">
        {(
          [
            ["group", "소집단 등록"],
            ["plan", "활동 계획서"],
            ["record", "활동 기록서"],
            ["evaluation", "활동 평가표"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`quality-tabs__btn${tab === key ? " is-active" : ""}`}
            onClick={() => {
              setTab(key);
              resetFormsOnTab();
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="quality-error">{error}</p>}
      {approvalMessage ? (
        <p className="ca-approval-msg">{approvalMessage}</p>
      ) : null}

      {tab === "group" && (
        <>
          {!showGroupForm ? (
            <>
              <div className="quality-shell__toolbar">
                <input
                  value={groupKeyword}
                  onChange={(e) => setGroupKeyword(e.target.value)}
                  placeholder="번호 / 소집단명 / 리더 검색"
                  style={{
                    height: 34,
                    padding: "0 10px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                  }}
                />
                <button
                  type="button"
                  className="ncr-btn is-primary"
                  onClick={() => void openGroupCreate()}
                >
                  소집단 등록
                </button>
              </div>
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>소집단명</th>
                      <th>개선주제</th>
                      <th>리더</th>
                      <th>인원</th>
                      <th>구성일</th>
                      <th>상태</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.length === 0 ? (
                      <tr>
                        <td colSpan={8}>등록된 소집단이 없습니다.</td>
                      </tr>
                    ) : (
                      groups.map((row) => (
                        <tr
                          key={row.groupId}
                          onClick={() => openGroupDetail(row)}
                        >
                          <td>{row.groupNo || "-"}</td>
                          <td>{row.groupName}</td>
                          <td>{row.theme || "-"}</td>
                          <td>{row.leaderName || "-"}</td>
                          <td>
                            {row.memberCount != null
                              ? `${row.memberCount}명`
                              : row.members
                                ? row.members.split(",").length
                                : "-"}
                          </td>
                          <td>{row.formedDate || "-"}</td>
                          <td>
                            <span
                              className={`quality-badge is-${row.approvalInProgress ? "pending" : (row.status || "active").toLowerCase()}`}
                            >
                              {row.approvalInProgress
                                ? "결재 요청 중"
                                : isApproved(row.status)
                                  ? "승인완료"
                                  : row.status === "CLOSED"
                                    ? "해체"
                                    : "활동중"}
                            </span>
                          </td>
                          <td>
                            {!isApproved(row.status) &&
                            !row.approvalInProgress ? (
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (
                                    !window.confirm(
                                      "소집단과 관련 계획서·기록서·평가표가 함께 삭제됩니다. 계속할까요?",
                                    )
                                  )
                                    return;
                                  void deleteSmallGroup(row.groupId).then(
                                    () => {
                                      void loadGroups(groupKeyword);
                                      void loadPlans(planFilterGroupId);
                                      void loadRecords(recordFilterGroupId);
                                      void loadEvaluations(evalFilterGroupId);
                                    },
                                  );
                                }}
                              >
                                삭제
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <form
              className="quality-form"
              onSubmit={isGroupEditing ? saveGroup : (e) => e.preventDefault()}
            >
              {isGroupDetail ? (
                <div
                  className="quality-shell__toolbar"
                  style={{ marginBottom: 12 }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>소집단 상세</h3>
                    <p
                      className="quality-form__hint"
                      style={{ margin: "4px 0 0" }}
                    >
                      {groupForm.groupNo || "-"} ·{" "}
                      {groupStamp.approvalInProgress
                        ? "결재 요청 중"
                        : isApproved(groupForm.status)
                          ? "승인완료"
                          : groupForm.status === "CLOSED"
                            ? "해체"
                            : "활동중"}
                    </p>
                  </div>
                  {renderGroupDetailActions()}
                </div>
              ) : (
                renderGroupEditActions()
              )}
              <fieldset
                disabled={isGroupDetail}
                style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
              >
                <section className="quality-form__section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <h3 style={{ margin: 0 }}>
                        {groupView.type === "create"
                          ? "소집단 등록"
                          : groupView.type === "edit"
                            ? "소집단 수정"
                            : "소집단 정보"}
                      </h3>
                      {isGroupEditing ? (
                        <p
                          className="quality-form__hint"
                          style={{ margin: "4px 0 0" }}
                        >
                          저장하면 상세보기로 이동합니다.
                        </p>
                      ) : null}
                    </div>
                    <StampBox
                      authorName={groupStamp.authorName}
                      reviewerName={groupStamp.reviewerName}
                      approverName={groupStamp.approverName}
                    />
                  </div>
                  <div className="quality-form__grid">
                    <label>
                      소집단번호
                      <input
                        value={
                          groupEditId == null ? "" : groupForm.groupNo || ""
                        }
                        readOnly
                        placeholder={
                          groupEditId == null ? "저장 시 자동 발급" : undefined
                        }
                      />
                      {groupEditId == null && groupHint ? (
                        <span className="quality-form__hint">{groupHint}</span>
                      ) : null}
                    </label>
                    <label>
                      구성일
                      <input
                        type="date"
                        value={groupForm.formedDate || ""}
                        onChange={(e) =>
                          setGroupForm((p) => ({
                            ...p,
                            formedDate: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      소집단명 *
                      <input
                        value={groupForm.groupName}
                        onChange={(e) =>
                          setGroupForm((p) => ({
                            ...p,
                            groupName: e.target.value,
                          }))
                        }
                        required
                      />
                    </label>
                    <label className="is-span-2">
                      개선주제
                      <input
                        value={groupForm.theme || ""}
                        onChange={(e) =>
                          setGroupForm((p) => ({ ...p, theme: e.target.value }))
                        }
                        placeholder="예: 불량률 감소, 작업 표준화"
                      />
                    </label>
                    <label>
                      상태
                      <select
                        value={groupForm.status || "ACTIVE"}
                        onChange={(e) =>
                          setGroupForm((p) => ({
                            ...p,
                            status: e.target.value,
                          }))
                        }
                      >
                        <option value="ACTIVE">활동중</option>
                        <option value="CLOSED">해체</option>
                      </select>
                    </label>
                    <label>
                      해체일
                      <input
                        type="date"
                        value={groupForm.closedDate || ""}
                        disabled={groupForm.status !== "CLOSED"}
                        onChange={(e) =>
                          setGroupForm((p) => ({
                            ...p,
                            closedDate: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      회의주기
                      <input
                        value={groupForm.meetingCycle || ""}
                        onChange={(e) =>
                          setGroupForm((p) => ({
                            ...p,
                            meetingCycle: e.target.value,
                          }))
                        }
                        placeholder="예: 주 1회 / 격주"
                      />
                    </label>
                    <label>
                      회의장소
                      <input
                        value={groupForm.meetingPlace || ""}
                        onChange={(e) =>
                          setGroupForm((p) => ({
                            ...p,
                            meetingPlace: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      활동 목적
                      <textarea
                        rows={2}
                        value={groupForm.purpose || ""}
                        onChange={(e) =>
                          setGroupForm((p) => ({
                            ...p,
                            purpose: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      활동 목표
                      <textarea
                        rows={2}
                        value={groupForm.activityGoal || ""}
                        onChange={(e) =>
                          setGroupForm((p) => ({
                            ...p,
                            activityGoal: e.target.value,
                          }))
                        }
                        placeholder="정량/정성 목표"
                      />
                    </label>
                  </div>
                </section>

                <section className="quality-form__section">
                  <h3>리더 *</h3>
                  <p className="quality-form__hint">
                    회사 직원 중 1명을 선택합니다.
                  </p>
                  {orgMembers.length === 0 ? (
                    <p className="quality-form__hint">
                      선택 가능한 직원이 없습니다. 조직관리에서 직원을 확인해
                      주세요.
                    </p>
                  ) : (
                    <div className="sg-member-picker">
                      {orgMembers.map((m) => {
                        const selected = groupForm.leaderUserId === m.userId;
                        return (
                          <label
                            key={`leader-${m.userId}`}
                            className={`sg-member-picker__item${selected ? " is-selected" : ""}`}
                          >
                            <input
                              type="radio"
                              name="sg-leader"
                              checked={selected}
                              onChange={() =>
                                setGroupForm((p) => {
                                  const org = orgMembers.find(
                                    (x) => x.userId === m.userId,
                                  );
                                  return {
                                    ...p,
                                    leaderUserId: m.userId,
                                    members: org
                                      ? upsertOrgMember(p.members || [], org)
                                      : p.members || [],
                                  };
                                })
                              }
                            />
                            <span className="sg-member-picker__avatar">
                              {memberName(m).slice(0, 1)}
                            </span>
                            <span className="sg-member-picker__text">
                              <strong>{memberName(m)}</strong>
                              <span>{memberMeta(m)}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </section>

                <section className="quality-form__section">
                  <h3>간사</h3>
                  <p className="quality-form__hint">
                    선택 사항입니다. 미지정 가능합니다.
                  </p>
                  <div className="sg-member-picker">
                    <label
                      className={`sg-member-picker__item${groupForm.secretaryUserId == null ? " is-selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="sg-secretary"
                        checked={groupForm.secretaryUserId == null}
                        onChange={() =>
                          setGroupForm((p) => ({ ...p, secretaryUserId: null }))
                        }
                      />
                      <span className="sg-member-picker__text">
                        <strong>미지정</strong>
                      </span>
                    </label>
                    {orgMembers.map((m) => {
                      const selected = groupForm.secretaryUserId === m.userId;
                      return (
                        <label
                          key={`sec-${m.userId}`}
                          className={`sg-member-picker__item${selected ? " is-selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name="sg-secretary"
                            checked={selected}
                            onChange={() =>
                              setGroupForm((p) => {
                                const org = orgMembers.find(
                                  (x) => x.userId === m.userId,
                                );
                                return {
                                  ...p,
                                  secretaryUserId: m.userId,
                                  members: org
                                    ? upsertOrgMember(p.members || [], org)
                                    : p.members || [],
                                };
                              })
                            }
                          />
                          <span className="sg-member-picker__avatar">
                            {memberName(m).slice(0, 1)}
                          </span>
                          <span className="sg-member-picker__text">
                            <strong>{memberName(m)}</strong>
                            <span>{memberMeta(m)}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>

                <section className="quality-form__section">
                  <h3>구성원</h3>
                  <p className="quality-form__hint">
                    가입 직원을 선택하거나, 미가입자는 아래에서 직접
                    입력합니다. 리더·간사는 자동으로 포함됩니다.
                  </p>
                  <input
                    className="sg-member-search"
                    value={memberQuery}
                    onChange={(e) => setMemberQuery(e.target.value)}
                    placeholder="이름 / 사번 / 부서 검색"
                    disabled={isGroupDetail}
                  />
                  <div className="sg-member-picker">
                    {filteredOrgMembers.length === 0 ? (
                      <p className="quality-form__hint">
                        검색 결과가 없습니다.
                      </p>
                    ) : (
                      filteredOrgMembers.map((m) => {
                        const checked = (groupForm.members || []).some(
                          (x) => x.userId === m.userId,
                        );
                        const lockedRole =
                          m.userId === groupForm.leaderUserId ||
                          m.userId === groupForm.secretaryUserId;
                        return (
                          <label
                            key={`mem-${m.userId}`}
                            className={`sg-member-picker__item${checked ? " is-selected" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isGroupDetail || lockedRole}
                              onChange={() =>
                                setGroupForm((p) => {
                                  const exists = (p.members || []).some(
                                    (x) => x.userId === m.userId,
                                  );
                                  return {
                                    ...p,
                                    members: exists
                                      ? (p.members || []).filter(
                                          (x) => x.userId !== m.userId,
                                        )
                                      : upsertOrgMember(p.members || [], m),
                                  };
                                })
                              }
                            />
                            <span className="sg-member-picker__avatar">
                              {memberName(m).slice(0, 1)}
                            </span>
                            <span className="sg-member-picker__text">
                              <strong>
                                {memberName(m)}
                                {m.userId === groupForm.leaderUserId
                                  ? " (리더)"
                                  : m.userId === groupForm.secretaryUserId
                                    ? " (간사)"
                                    : ""}
                              </strong>
                              <span>{memberMeta(m)}</span>
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>

                  {!isGroupDetail ? (
                    <div className="sg-manual-member">
                      <strong>미가입자 직접 입력</strong>
                      <div className="sg-manual-member__row">
                        <input
                          value={manualMemberName}
                          onChange={(e) => setManualMemberName(e.target.value)}
                          placeholder="성명 *"
                        />
                        <input
                          value={manualMemberDept}
                          onChange={(e) => setManualMemberDept(e.target.value)}
                          placeholder="부서 (선택)"
                        />
                        <button
                          type="button"
                          className="ncr-btn is-secondary"
                          onClick={() => {
                            const name = manualMemberName.trim();
                            if (!name) {
                              reportError(setError, "성명을 입력해 주세요.");
                              return;
                            }
                            const dup = (groupForm.members || []).some(
                              (x) =>
                                x.manualEntry &&
                                x.name.trim().toLowerCase() ===
                                  name.toLowerCase() &&
                                (x.dept || "").trim().toLowerCase() ===
                                  manualMemberDept.trim().toLowerCase(),
                            );
                            if (dup) {
                              reportError(
                                setError,
                                "이미 추가된 직접 입력 구성원입니다.",
                              );
                              return;
                            }
                            setGroupForm((p) => ({
                              ...p,
                              members: [
                                ...(p.members || []),
                                createManualSmallGroupMember(
                                  name,
                                  manualMemberDept,
                                ),
                              ],
                            }));
                            setManualMemberName("");
                            setManualMemberDept("");
                            setError("");
                          }}
                        >
                          추가
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="sg-member-chips">
                    {(groupForm.members || []).map((mem) => {
                      const lockedRole =
                        mem.userId != null &&
                        (mem.userId === groupForm.leaderUserId ||
                          mem.userId === groupForm.secretaryUserId);
                      const label = mem.dept
                        ? `${mem.name} · ${mem.dept}`
                        : mem.name;
                      const roleTag =
                        mem.userId === groupForm.leaderUserId
                          ? "리더"
                          : mem.userId === groupForm.secretaryUserId
                            ? "간사"
                            : mem.manualEntry
                              ? "직접입력"
                              : null;
                      return (
                        <span
                          key={mem.id}
                          className={`sg-member-chip${mem.manualEntry ? " is-manual" : ""}`}
                        >
                          {label}
                          {roleTag ? (
                            <em className="sg-member-chip__tag">{roleTag}</em>
                          ) : null}
                          {!isGroupDetail && !lockedRole ? (
                            <button
                              type="button"
                              aria-label={`${label} 제거`}
                              onClick={() =>
                                setGroupForm((p) => ({
                                  ...p,
                                  members: removeMember(p.members || [], mem),
                                }))
                              }
                            >
                              ×
                            </button>
                          ) : null}
                        </span>
                      );
                    })}
                  </div>
                </section>

                <section className="quality-form__section">
                  <h3>비고</h3>
                  <div className="quality-form__grid">
                    <label className="is-span-2">
                      비고
                      <textarea
                        rows={2}
                        value={groupForm.remark || ""}
                        onChange={(e) =>
                          setGroupForm((p) => ({
                            ...p,
                            remark: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                </section>
              </fieldset>
              {isGroupDetail ? (
                <div className="quality-form__actions">
                  {renderGroupDetailActions()}
                </div>
              ) : (
                renderGroupEditActions()
              )}
              {groupEditId != null ? (
                <ApprovalRequestModal
                  open={groupApprovalOpen}
                  titleDefault={`${groupForm.groupName || "소집단"} 결재`}
                  targetType={APPROVAL_TARGET_SMALL_GROUP}
                  targetId={groupEditId}
                  onClose={() => setGroupApprovalOpen(false)}
                  onSubmitted={() => {
                    setGroupApprovalOpen(false);
                    setApprovalMessage(
                      "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                    );
                    void loadGroups(groupKeyword);
                  }}
                />
              ) : null}
            </form>
          )}
        </>
      )}

      {tab === "plan" && (
        <>
          {!showPlanForm ? (
            <>
              <div className="quality-shell__toolbar">
                {groupFilter(planFilterGroupId, setPlanFilterGroupId)}
                <button
                  type="button"
                  className="ncr-btn is-primary"
                  onClick={() => void openPlanCreate()}
                  disabled={groups.length === 0}
                >
                  계획서 등록
                </button>
              </div>
              {groups.length === 0 && (
                <p className="quality-form__hint">
                  먼저 소집단을 등록해 주세요.
                </p>
              )}
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>연도</th>
                      <th>소집단</th>
                      <th>제목</th>
                      <th>상태</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.length === 0 ? (
                      <tr>
                        <td colSpan={6}>등록된 계획서가 없습니다.</td>
                      </tr>
                    ) : (
                      plans.map((row) => (
                        <tr
                          key={row.planId}
                          onClick={() => openPlanDetail(row)}
                        >
                          <td>{row.planNo || "-"}</td>
                          <td>{row.planYear || "-"}</td>
                          <td>{row.groupName || "-"}</td>
                          <td>{row.title || "-"}</td>
                          <td>
                            <span
                              className={`quality-badge is-${row.approvalInProgress ? "pending" : (row.status || "draft").toLowerCase()}`}
                            >
                              {row.approvalInProgress
                                ? "결재 요청 중"
                                : isApproved(row.status)
                                  ? "승인완료"
                                  : row.status === "CONFIRMED"
                                    ? "확정"
                                    : "작성중"}
                            </span>
                          </td>
                          <td>
                            {!isApproved(row.status) &&
                            !row.approvalInProgress ? (
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!window.confirm("삭제할까요?")) return;
                                  void deleteSmallGroupPlan(row.planId).then(
                                    () => loadPlans(planFilterGroupId),
                                  );
                                }}
                              >
                                삭제
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <form
              className="quality-form"
              onSubmit={isPlanEditing ? savePlan : (e) => e.preventDefault()}
            >
              {isPlanDetail ? (
                <div
                  className="quality-shell__toolbar"
                  style={{ marginBottom: 12 }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>계획서 상세</h3>
                    <p
                      className="quality-form__hint"
                      style={{ margin: "4px 0 0" }}
                    >
                      {planForm.planNo || "-"} ·{" "}
                      {planStamp.approvalInProgress
                        ? "결재 요청 중"
                        : isApproved(planForm.status)
                          ? "승인완료"
                          : "작성중"}
                    </p>
                  </div>
                  {renderPlanDetailActions()}
                </div>
              ) : (
                renderPlanEditActions()
              )}
              <fieldset
                disabled={isPlanDetail}
                style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
              >
                <section className="quality-form__section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <h3 style={{ margin: 0 }}>활동 계획서</h3>
                    <StampBox
                      authorName={planStamp.authorName}
                      reviewerName={planStamp.reviewerName}
                      approverName={planStamp.approverName}
                    />
                  </div>
                  <div className="quality-form__grid">
                    <label>
                      계획서번호
                      <input
                        value={planEditId == null ? "" : planForm.planNo || ""}
                        readOnly
                        placeholder={
                          planEditId == null ? "저장 시 자동 발급" : undefined
                        }
                      />
                      {planEditId == null && planHint ? (
                        <span className="quality-form__hint">{planHint}</span>
                      ) : null}
                    </label>
                    <label>
                      소집단 *
                      {groupSelect(planForm.groupId, (groupId) =>
                        setPlanForm((p) => ({ ...p, groupId })),
                      )}
                    </label>
                    <label>
                      연도
                      <input
                        type="number"
                        min={2000}
                        max={2100}
                        value={planForm.planYear || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            planYear: Number(e.target.value),
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      제목
                      <input
                        value={planForm.title || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({ ...p, title: e.target.value }))
                        }
                      />
                    </label>
                    <label>
                      기간 시작
                      <input
                        type="date"
                        value={planForm.periodFrom || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            periodFrom: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      기간 종료
                      <input
                        type="date"
                        value={planForm.periodTo || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            periodTo: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      작성자
                      <input
                        value={planForm.preparedBy || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            preparedBy: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      목표
                      <textarea
                        rows={3}
                        value={planForm.objective || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            objective: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      기대효과
                      <textarea
                        rows={2}
                        value={planForm.expectedEffect || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            expectedEffect: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        marginBottom: 8,
                        gap: 12,
                      }}
                    >
                      <div>
                        <strong>활동 항목</strong>
                        <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                          주제·시기·방법·담당을 한 줄로 입력합니다. · {planItems.length}건
                        </p>
                      </div>
                    </div>
                    <div className="edu-plan-table-wrap sg-plan-items-wrap">
                      <table className="edu-plan-table sg-plan-items-table">
                        <thead>
                          <tr>
                            <th className="col-no">No</th>
                            <th className="col-topic">주제/활동</th>
                            <th className="col-schedule">시기</th>
                            <th className="col-method">방법</th>
                            <th className="col-owner">담당</th>
                            {!isPlanDetail ? <th className="col-actions" /> : null}
                          </tr>
                        </thead>
                        <tbody>
                          {planItems.length === 0 ? (
                            <tr>
                              <td
                                colSpan={isPlanDetail ? 5 : 6}
                                className="sg-plan-items-empty"
                              >
                                등록된 활동 항목이 없습니다.
                              </td>
                            </tr>
                          ) : (
                            planItems.map((item, idx) => (
                              <tr key={item.id}>
                                <td className="is-center col-no">{idx + 1}</td>
                                <td className="col-topic">
                                  <input
                                    value={item.topic}
                                    placeholder="주제/활동"
                                    onChange={(e) =>
                                      setPlanItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? { ...r, topic: e.target.value }
                                            : r,
                                        ),
                                      )
                                    }
                                  />
                                </td>
                                <td className="col-schedule">
                                  <input
                                    value={item.schedule}
                                    placeholder="시기"
                                    onChange={(e) =>
                                      setPlanItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? { ...r, schedule: e.target.value }
                                            : r,
                                        ),
                                      )
                                    }
                                  />
                                </td>
                                <td className="col-method">
                                  <input
                                    value={item.method}
                                    placeholder="방법"
                                    onChange={(e) =>
                                      setPlanItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? { ...r, method: e.target.value }
                                            : r,
                                        ),
                                      )
                                    }
                                  />
                                </td>
                                <td className="col-owner">
                                  <input
                                    value={item.owner}
                                    placeholder="담당"
                                    onChange={(e) =>
                                      setPlanItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? { ...r, owner: e.target.value }
                                            : r,
                                        ),
                                      )
                                    }
                                  />
                                </td>
                                {!isPlanDetail ? (
                                  <td className="col-actions">
                                    <button
                                      type="button"
                                      className="ncr-btn is-danger sg-plan-items-del"
                                      onClick={() =>
                                        setPlanItems((prev) =>
                                          prev.filter((_, i) => i !== idx),
                                        )
                                      }
                                    >
                                      삭제
                                    </button>
                                  </td>
                                ) : null}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {!isPlanDetail ? (
                      <div className="edu-plan-table-footer">
                        <button
                          type="button"
                          className="ncr-btn is-secondary"
                          onClick={() =>
                            setPlanItems((prev) => [
                              ...prev,
                              {
                                id: newId(),
                                topic: "",
                                schedule: "",
                                method: "",
                                owner: "",
                              },
                            ])
                          }
                        >
                          항목 추가
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div className="quality-form__grid" style={{ marginTop: 12 }}>
                    <label className="is-span-2">
                      비고
                      <textarea
                        rows={2}
                        value={planForm.remark || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({ ...p, remark: e.target.value }))
                        }
                      />
                    </label>
                  </div>
                </section>
              </fieldset>
              {isPlanDetail ? (
                <div className="quality-form__actions">
                  {renderPlanDetailActions()}
                </div>
              ) : (
                renderPlanEditActions()
              )}
              {planEditId != null ? (
                <ApprovalRequestModal
                  open={planApprovalOpen}
                  titleDefault={`${planForm.title || "소집단 활동 계획서"} 결재`}
                  targetType={APPROVAL_TARGET_SMALL_GROUP_PLAN}
                  targetId={planEditId}
                  onClose={() => setPlanApprovalOpen(false)}
                  onSubmitted={() => {
                    setPlanApprovalOpen(false);
                    setApprovalMessage(
                      "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                    );
                    void loadPlans(planFilterGroupId);
                  }}
                />
              ) : null}
            </form>
          )}
        </>
      )}

      {tab === "record" && (
        <>
          {!showRecordForm ? (
            <>
              <div className="quality-shell__toolbar">
                {groupFilter(recordFilterGroupId, setRecordFilterGroupId)}
                <button
                  type="button"
                  className="ncr-btn is-primary"
                  onClick={() => void openRecordCreate()}
                  disabled={groups.length === 0}
                >
                  기록서 등록
                </button>
              </div>
              {groups.length === 0 && (
                <p className="quality-form__hint">
                  먼저 소집단을 등록해 주세요.
                </p>
              )}
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>일자</th>
                      <th>소집단</th>
                      <th>활동명</th>
                      <th>상태</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 ? (
                      <tr>
                        <td colSpan={6}>등록된 기록서가 없습니다.</td>
                      </tr>
                    ) : (
                      records.map((row) => (
                        <tr
                          key={row.activityId}
                          onClick={() => openRecordDetail(row)}
                        >
                          <td>{row.recordNo || "-"}</td>
                          <td>{row.activityDate || "-"}</td>
                          <td>{row.groupName || "-"}</td>
                          <td>{row.title || "-"}</td>
                          <td>
                            <span
                              className={`quality-badge is-${row.approvalInProgress ? "pending" : (row.status || "draft").toLowerCase()}`}
                            >
                              {row.approvalInProgress
                                ? "결재 요청 중"
                                : isApproved(row.status)
                                  ? "승인완료"
                                  : row.status === "DONE"
                                    ? "완료"
                                    : "작성중"}
                            </span>
                          </td>
                          <td>
                            {!isApproved(row.status) &&
                            !row.approvalInProgress ? (
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!window.confirm("삭제할까요?")) return;
                                  void deleteSmallGroupActivity(
                                    row.activityId,
                                  ).then(() =>
                                    loadRecords(recordFilterGroupId),
                                  );
                                }}
                              >
                                삭제
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <form
              className="quality-form"
              onSubmit={
                isRecordEditing ? saveRecord : (e) => e.preventDefault()
              }
            >
              {isRecordDetail ? (
                <div
                  className="quality-shell__toolbar"
                  style={{ marginBottom: 12 }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>기록서 상세</h3>
                    <p
                      className="quality-form__hint"
                      style={{ margin: "4px 0 0" }}
                    >
                      {recordForm.recordNo || "-"} ·{" "}
                      {recordStamp.approvalInProgress
                        ? "결재 요청 중"
                        : isApproved(recordForm.status)
                          ? "승인완료"
                          : "작성중"}
                    </p>
                  </div>
                  {renderRecordDetailActions()}
                </div>
              ) : (
                renderRecordEditActions()
              )}
              <fieldset
                disabled={isRecordDetail}
                style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
              >
                <section className="quality-form__section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <h3 style={{ margin: 0 }}>활동 기록서</h3>
                    <StampBox
                      authorName={recordStamp.authorName}
                      reviewerName={recordStamp.reviewerName}
                      approverName={recordStamp.approverName}
                    />
                  </div>
                  <div className="quality-form__grid">
                    <label>
                      기록번호
                      <input
                        value={
                          recordEditId == null ? "" : recordForm.recordNo || ""
                        }
                        readOnly
                        placeholder={
                          recordEditId == null ? "저장 시 자동 발급" : undefined
                        }
                      />
                      {recordEditId == null && recordHint ? (
                        <span className="quality-form__hint">{recordHint}</span>
                      ) : null}
                    </label>
                    <label>
                      소집단 *
                      {groupSelect(recordForm.groupId, (groupId) =>
                        setRecordForm((p) => ({ ...p, groupId })),
                      )}
                    </label>
                    <label>
                      활동일
                      <input
                        type="date"
                        value={recordForm.activityDate || ""}
                        onChange={(e) =>
                          setRecordForm((p) => ({
                            ...p,
                            activityDate: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      활동명 *
                      <input
                        value={recordForm.title}
                        onChange={(e) =>
                          setRecordForm((p) => ({
                            ...p,
                            title: e.target.value,
                          }))
                        }
                        required
                      />
                    </label>
                    <label>
                      장소
                      <input
                        value={recordForm.location || ""}
                        onChange={(e) =>
                          setRecordForm((p) => ({
                            ...p,
                            location: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                </section>

                <section className="quality-form__section">
                  <div className="quality-form__grid">
                    <label className="is-span-2">
                      활동내용
                      <textarea
                        rows={4}
                        value={recordForm.content || ""}
                        onChange={(e) =>
                          setRecordForm((p) => ({
                            ...p,
                            content: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      결과
                      <textarea
                        rows={3}
                        value={recordForm.resultText || ""}
                        onChange={(e) =>
                          setRecordForm((p) => ({
                            ...p,
                            resultText: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      다음 계획
                      <textarea
                        rows={3}
                        value={recordForm.nextPlan || ""}
                        onChange={(e) =>
                          setRecordForm((p) => ({
                            ...p,
                            nextPlan: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      비고
                      <textarea
                        rows={2}
                        value={recordForm.remark || ""}
                        onChange={(e) =>
                          setRecordForm((p) => ({
                            ...p,
                            remark: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                </section>

                <section className="quality-form__section">
                  <div className="ca-section-head">
                    <h3>참석자</h3>
                  </div>
                  <div className="ca-items">
                    {recordAttendees.map((row, idx) => (
                      <div className="ca-item-card" key={row.id}>
                        <div className="ca-item-card__head">
                          <strong>참석자 {idx + 1}</strong>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            {!recordLocked ? (
                              <button
                                type="button"
                                className="ncr-btn is-secondary"
                                onClick={() =>
                                  patchRecordAttendee(idx, {
                                    manualEntry: !row.manualEntry,
                                    userId: null,
                                    departmentId: null,
                                    name: row.manualEntry ? "" : row.name,
                                    dept: row.manualEntry ? "" : row.dept,
                                  })
                                }
                              >
                                {row.manualEntry
                                  ? "조직에서 선택"
                                  : "직접 입력"}
                              </button>
                            ) : null}
                            {!recordLocked ? (
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                onClick={() =>
                                  setRecordAttendees((prev) => {
                                    const next = prev.filter((_, i) => i !== idx);
                                    return next.length
                                      ? next
                                      : defaultEducationAttendees();
                                  })
                                }
                              >
                                삭제
                              </button>
                            ) : null}
                          </div>
                        </div>
                        <div className="ca-item-card__grid">
                          <label className="is-wide">
                            부서
                            {recordLocked || row.manualEntry ? (
                              <input
                                value={row.dept}
                                readOnly={recordLocked}
                                placeholder="부서"
                                onChange={(e) =>
                                  patchRecordAttendee(idx, {
                                    dept: e.target.value,
                                    manualEntry: true,
                                    userId: null,
                                    departmentId: null,
                                  })
                                }
                              />
                            ) : (
                              <select
                                value={recordDeptKeyOfRow(row)}
                                onChange={(e) => {
                                  const key = e.target.value;
                                  const opt = recordDepartmentOptions.find(
                                    (d) => d.key === key,
                                  );
                                  patchRecordAttendee(idx, {
                                    departmentId: opt?.departmentId ?? null,
                                    dept: opt?.label || "",
                                    userId: null,
                                    name: "",
                                    manualEntry: false,
                                  });
                                }}
                              >
                                <option value="">부서 선택</option>
                                {recordDepartmentOptions.map((d) => (
                                  <option key={d.key} value={d.key}>
                                    {d.label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </label>
                          <label className="is-wide">
                            성명
                            {recordLocked || row.manualEntry ? (
                              <input
                                value={row.name}
                                readOnly={recordLocked}
                                placeholder="성명"
                                onChange={(e) =>
                                  patchRecordAttendee(idx, {
                                    name: e.target.value,
                                    manualEntry: true,
                                    userId: null,
                                  })
                                }
                              />
                            ) : (
                              <select
                                value={
                                  row.userId != null ? String(row.userId) : ""
                                }
                                disabled={!recordDeptKeyOfRow(row)}
                                onChange={(e) => {
                                  const uid = e.target.value
                                    ? Number(e.target.value)
                                    : null;
                                  if (uid == null) {
                                    patchRecordAttendee(idx, {
                                      userId: null,
                                      name: "",
                                    });
                                    return;
                                  }
                                  const m = orgMembers.find(
                                    (x) => x.userId === uid,
                                  );
                                  if (!m) return;
                                  patchRecordAttendee(idx, {
                                    userId: m.userId,
                                    name: memberName(m),
                                    departmentId: m.departmentId ?? null,
                                    dept: m.departmentName?.trim() || "미배치",
                                    manualEntry: false,
                                  });
                                }}
                              >
                                <option value="">
                                  {recordDeptKeyOfRow(row)
                                    ? "성명 선택"
                                    : "부서 먼저 선택"}
                                </option>
                                {recordDeptKeyOfRow(row)
                                  ? recordMembersByDeptKey(
                                      recordDeptKeyOfRow(row),
                                    ).map((m) => {
                                      const taken =
                                        recordSelectedUserIds.has(m.userId) &&
                                        row.userId !== m.userId;
                                      return (
                                        <option
                                          key={m.userId}
                                          value={m.userId}
                                          disabled={taken}
                                        >
                                          {memberName(m)}
                                          {m.jobTitle
                                            ? ` · ${m.jobTitle}`
                                            : ""}
                                          {taken ? " (이미 추가됨)" : ""}
                                        </option>
                                      );
                                    })
                                  : null}
                              </select>
                            )}
                          </label>
                          <div className="is-full">
                            <SignaturePadField
                              label="참석 서명"
                              value={row.signatureData}
                              disabled={recordLocked}
                              onChange={(signatureData) =>
                                patchRecordAttendee(idx, { signatureData })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {!recordLocked ? (
                    <div style={{ marginTop: 12 }}>
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        onClick={() =>
                          setRecordAttendees((prev) => [
                            ...prev,
                            createEmptyEducationAttendee(),
                          ])
                        }
                      >
                        참석자 추가
                      </button>
                    </div>
                  ) : null}
                </section>
              </fieldset>
              {isRecordDetail ? (
                <div className="quality-form__actions">
                  {renderRecordDetailActions()}
                </div>
              ) : (
                renderRecordEditActions()
              )}
              {recordEditId != null ? (
                <ApprovalRequestModal
                  open={recordApprovalOpen}
                  titleDefault={`${recordForm.title || "소집단 활동 기록서"} 결재`}
                  targetType={APPROVAL_TARGET_SMALL_GROUP_RECORD}
                  targetId={recordEditId}
                  onClose={() => setRecordApprovalOpen(false)}
                  onSubmitted={() => {
                    setRecordApprovalOpen(false);
                    setApprovalMessage(
                      "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                    );
                    void loadRecords(recordFilterGroupId);
                  }}
                />
              ) : null}
            </form>
          )}
        </>
      )}

      {tab === "evaluation" && (
        <>
          {!showEvalForm ? (
            <>
              <div className="quality-shell__toolbar">
                {groupFilter(evalFilterGroupId, setEvalFilterGroupId)}
                <button
                  type="button"
                  className="ncr-btn is-primary"
                  onClick={() => void openEvalCreate()}
                  disabled={groups.length === 0}
                >
                  평가표 등록
                </button>
              </div>
              {groups.length === 0 && (
                <p className="quality-form__hint">
                  먼저 소집단을 등록해 주세요.
                </p>
              )}
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>평가일</th>
                      <th>소집단</th>
                      <th>제목</th>
                      <th>상태</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evaluations.length === 0 ? (
                      <tr>
                        <td colSpan={6}>등록된 평가표가 없습니다.</td>
                      </tr>
                    ) : (
                      evaluations.map((row) => (
                        <tr
                          key={row.evaluationId}
                          onClick={() => openEvalDetail(row)}
                        >
                          <td>{row.evaluationNo || "-"}</td>
                          <td>{row.evaluationDate || "-"}</td>
                          <td>{row.groupName || "-"}</td>
                          <td>{row.title || "-"}</td>
                          <td>
                            <span
                              className={`quality-badge is-${row.approvalInProgress ? "pending" : (row.status || "draft").toLowerCase()}`}
                            >
                              {row.approvalInProgress
                                ? "결재 요청 중"
                                : isApproved(row.status)
                                  ? "승인완료"
                                  : row.status === "COMPLETED"
                                    ? "완료"
                                    : "작성중"}
                            </span>
                          </td>
                          <td>
                            {!isApproved(row.status) &&
                            !row.approvalInProgress ? (
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!window.confirm("삭제할까요?")) return;
                                  void deleteSmallGroupEvaluation(
                                    row.evaluationId,
                                  ).then(() =>
                                    loadEvaluations(evalFilterGroupId),
                                  );
                                }}
                              >
                                삭제
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <form
              className="quality-form"
              onSubmit={isEvalEditing ? saveEval : (e) => e.preventDefault()}
            >
              {isEvalDetail ? (
                <div
                  className="quality-shell__toolbar"
                  style={{ marginBottom: 12 }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>평가표 상세</h3>
                    <p
                      className="quality-form__hint"
                      style={{ margin: "4px 0 0" }}
                    >
                      {evalForm.evaluationNo || "-"} ·{" "}
                      {evalStamp.approvalInProgress
                        ? "결재 요청 중"
                        : isApproved(evalForm.status)
                          ? "승인완료"
                          : "작성중"}
                    </p>
                  </div>
                  {renderEvalDetailActions()}
                </div>
              ) : (
                renderEvalEditActions()
              )}
              <fieldset
                disabled={isEvalDetail}
                style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
              >
                <section className="quality-form__section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <h3 style={{ margin: 0 }}>활동 평가표</h3>
                    <StampBox
                      authorName={evalStamp.authorName}
                      reviewerName={evalStamp.reviewerName}
                      approverName={evalStamp.approverName}
                    />
                  </div>
                  <div className="quality-form__grid">
                    <label>
                      평가표번호
                      <input
                        value={
                          evalEditId == null ? "" : evalForm.evaluationNo || ""
                        }
                        readOnly
                        placeholder={
                          evalEditId == null ? "저장 시 자동 발급" : undefined
                        }
                      />
                      {evalEditId == null && evalHint ? (
                        <span className="quality-form__hint">{evalHint}</span>
                      ) : null}
                    </label>
                    <label>
                      소집단 *
                      {groupSelect(evalForm.groupId, (groupId) =>
                        setEvalForm((p) => ({ ...p, groupId })),
                      )}
                    </label>
                    <label>
                      평가일
                      <input
                        type="date"
                        value={evalForm.evaluationDate || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({
                            ...p,
                            evaluationDate: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      제목
                      <input
                        value={evalForm.title || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({ ...p, title: e.target.value }))
                        }
                      />
                    </label>
                    <label>
                      대상기간 시작
                      <input
                        type="date"
                        value={evalForm.periodFrom || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({
                            ...p,
                            periodFrom: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      대상기간 종료
                      <input
                        type="date"
                        value={evalForm.periodTo || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({
                            ...p,
                            periodTo: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      평가자
                      <input
                        value={evalForm.evaluator || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({
                            ...p,
                            evaluator: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      종합점수
                      <input
                        value={evalForm.scoreText || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({
                            ...p,
                            scoreText: e.target.value,
                          }))
                        }
                        placeholder="예: 85 / 100"
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 8,
                      }}
                    >
                      <strong>평가 항목</strong>
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        onClick={() =>
                          setEvalCriteria((prev) => [
                            ...prev,
                            {
                              id: newId(),
                              criterion: "",
                              score: "",
                              comment: "",
                            },
                          ])
                        }
                      >
                        항목 추가
                      </button>
                    </div>
                    {evalCriteria.map((item, idx) => (
                      <div
                        key={item.id}
                        className="quality-form__grid"
                        style={{
                          marginBottom: 10,
                          padding: 12,
                          border: "1px solid #e2e8f0",
                          borderRadius: 8,
                        }}
                      >
                        <label className="is-span-2">
                          평가항목
                          <textarea
                            rows={2}
                            value={item.criterion}
                            onChange={(e) =>
                              setEvalCriteria((prev) =>
                                prev.map((r, i) =>
                                  i === idx
                                    ? { ...r, criterion: e.target.value }
                                    : r,
                                ),
                              )
                            }
                          />
                        </label>
                        <label>
                          점수
                          <input
                            value={item.score}
                            onChange={(e) =>
                              setEvalCriteria((prev) =>
                                prev.map((r, i) =>
                                  i === idx
                                    ? { ...r, score: e.target.value }
                                    : r,
                                ),
                              )
                            }
                          />
                        </label>
                        <label className="is-span-2">
                          의견
                          <textarea
                            rows={2}
                            value={item.comment}
                            onChange={(e) =>
                              setEvalCriteria((prev) =>
                                prev.map((r, i) =>
                                  i === idx
                                    ? { ...r, comment: e.target.value }
                                    : r,
                                ),
                              )
                            }
                          />
                        </label>
                        <div>
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            onClick={() =>
                              setEvalCriteria((prev) =>
                                prev.filter((_, i) => i !== idx),
                              )
                            }
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="quality-form__grid" style={{ marginTop: 12 }}>
                    <label className="is-span-2">
                      강점
                      <textarea
                        rows={2}
                        value={evalForm.strengths || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({
                            ...p,
                            strengths: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      개선점
                      <textarea
                        rows={2}
                        value={evalForm.improvements || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({
                            ...p,
                            improvements: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      종합의견
                      <textarea
                        rows={3}
                        value={evalForm.overallComment || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({
                            ...p,
                            overallComment: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      비고
                      <textarea
                        rows={2}
                        value={evalForm.remark || ""}
                        onChange={(e) =>
                          setEvalForm((p) => ({ ...p, remark: e.target.value }))
                        }
                      />
                    </label>
                  </div>
                </section>
              </fieldset>
              {isEvalDetail ? (
                <div className="quality-form__actions">
                  {renderEvalDetailActions()}
                </div>
              ) : (
                renderEvalEditActions()
              )}
              {evalEditId != null ? (
                <ApprovalRequestModal
                  open={evalApprovalOpen}
                  titleDefault={`${evalForm.title || "소집단 활동 평가표"} 결재`}
                  targetType={APPROVAL_TARGET_SMALL_GROUP_EVAL}
                  targetId={evalEditId}
                  onClose={() => setEvalApprovalOpen(false)}
                  onSubmitted={() => {
                    setEvalApprovalOpen(false);
                    setApprovalMessage(
                      "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                    );
                    void loadEvaluations(evalFilterGroupId);
                  }}
                />
              ) : null}
            </form>
          )}
        </>
      )}
    </main>
  );
}

export default SmallGroupPanel;
